/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { ChangeEvent, useState, useEffect, useRef, useCallback } from 'react';
import { Seconds, AssetUtils } from '@wit/vtv-x-foundation-lib';

interface Props {
  restartAllowed: boolean;
  moveScrubberAllowed: boolean;
  sliderActive: boolean;
  duration: Seconds;
  progress: Seconds;
  liveProgress?: Seconds;
  seekingStream: boolean;
  handlePosition: (arg: Seconds | undefined) => void;
  onScrub?: (progress?: Seconds) => void;
  rules: {
    allowRewind: boolean;
    allowForward: boolean;
  };
}

enum MousePress {
  UP = 'up',
  DOWN = 'down',
  NONE = 'none',
}

export default function VideoRangeSlider({
  restartAllowed,
  moveScrubberAllowed,
  sliderActive,
  handlePosition,
  duration,
  progress,
  liveProgress,
  seekingStream,
  onScrub,
  rules,
}: Props): JSX.Element {
  const [sliderVal, setSliderVal] = useState(0);
  const [mouseState, setMouseState] = useState(MousePress.NONE);
  const startVal = useRef<number | undefined>(undefined);

  const validPosition = useCallback(
    (position: number) => {
      return (
        startVal.current !== undefined &&
        ((rules.allowForward && position >= startVal.current) || (rules.allowRewind && position <= startVal.current))
      );
    },
    [rules.allowForward, rules.allowRewind]
  );

  useEffect(() => {
    if (mouseState === MousePress.NONE && !seekingStream) {
      setSliderVal(progress);
    }
  }, [mouseState, progress, seekingStream]);

  useEffect(() => {
    if (mouseState === MousePress.DOWN && startVal.current === undefined) {
      startVal.current = sliderVal;
    }
  }, [mouseState, sliderVal]);

  const changeCallback = (e: ChangeEvent<HTMLInputElement>) => {
    const pos = Number(e.target.value);
    const newValue = liveProgress ? Math.min(pos, liveProgress) : pos;
    setSliderVal(newValue);
    if (onScrub && !Number.isNaN(newValue) && mouseState === MousePress.DOWN && validPosition(newValue)) {
      onScrub(newValue);
    }
  };

  useEffect(() => {
    if (mouseState === MousePress.UP) {
      if (validPosition(sliderVal)) {
        if (liveProgress && sliderVal > liveProgress) {
          handlePosition(liveProgress);
        } else {
          handlePosition(sliderVal);
        }
      } else {
        handlePosition(undefined);
      }
      setMouseState(MousePress.NONE);
      startVal.current = undefined;
      if (onScrub) {
        onScrub(undefined);
      }
    }
  }, [
    mouseState,
    sliderVal,
    handlePosition,
    liveProgress,
    duration,
    onScrub,
    rules.allowForward,
    rules.allowRewind,
    validPosition,
  ]);

  return (
    <div className="video-controls-progress-bar" id="VideoRangeSlider">
      <a className={`progress-bar ${restartAllowed ? '' : 'disabled'}`} id="VideoRangeSlider_progressBar">
        <input
          value={sliderVal}
          type="range"
          min="0"
          max={duration}
          onChange={changeCallback}
          onMouseDown={() => setMouseState(MousePress.DOWN)}
          onMouseUp={() => setMouseState(MousePress.UP)}
          id="VideoRangeSlider_rangeInput"
        />
        {moveScrubberAllowed && (
          <span
            className="progress-bar-scrubber slider"
            style={{ left: `${AssetUtils.getProgressInPercentage(sliderVal, duration)}%` }}
            id="VideoRangeSlider_scrubberSlider"
          />
        )}
      </a>
      <a
        className={`progress-bar-complete ${sliderActive ? 'slider-active' : ''} ${!restartAllowed ? 'disabled' : ''}`}
        style={{
          width: `${AssetUtils.getProgressInPercentage(sliderVal, duration)}%`,
        }}
        id="VideoRangeSlider_completeBar"
      />
      {!!liveProgress && (
        <a
          className="progress-bar-complete live"
          style={{ width: `${AssetUtils.getProgressInPercentage(liveProgress, duration)}%` }}
          id="VideoRangeSlider_liveBar"
        />
      )}
    </div>
  );
}
